import { flex, IconFont, Input, Select, singleTextEllipsis, Text } from '@byecode/ui'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { ArgumentsSchema } from '@lighthouse/core'
import { FlowNode, ListItem4ByecodeUi, TagIcon } from '@lighthouse/shared'
import { Collapse } from '@mantine/core'
import { JSONContent } from '@tiptap/core'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import { innerTypeOptions } from '@/containers/FlowDetail/constants'

import { SettingParam } from './SettingParam'

const SCxContainer = styled.div`
    width: 100%;
    border-radius: 8px;
    overflow: hidden;
    margin-top: 8px;
    border: 1px solid var(--color-gray-200);
`

const SCxHeader = styled.div`
    ${flex}
    padding: 12px 8px;
    justify-content: space-between;
    background-color: var(--color-gray-100);
    gap: 8px;
`

const SCxHeaderLeft = styled.div`
    ${flex}
    align-items: center;
    overflow: hidden;
    flex: 1;
`

const SCxHandle = styled.div`
    ${flex}
    align-items: center;
`

const SCxTitle = styled.span`
    padding-left: 4px;
    line-height: 16px;
    color: var(--color-black);
    font-style: normal;
    font-weight: 400;
    word-break: break-word;
    font-size: var(--font-size-normal);
    flex: 1;
    ${singleTextEllipsis};
    display: flex;
    align-items: center;
`

const SCxTypeTitle = styled.div`
    line-height: 16px;
    font-size: 14px;
    color: var(--color-gray-400);
    margin-left: 4px;
`

const SCxRightFill = styled.div`
    width: 180px;
    ${flex}
    justify-content: flex-end;
    align-items: center;
    gap: 7px;
`

const SCxListItemBaseInfo = styled.div`
    gap: 0;
    padding: 0 16px;
`

const SCxContent = styled.div`
    margin: 8px 0;
`

interface VariableItemControlledProps {
    item: ArgumentsSchema & { valuePath?: string; value?: JSONContent }
    opened: boolean
    onChangeOpen: (opened: boolean) => void
    onDelete: () => void
    onChange: (next: ArgumentsSchema & { valuePath?: string; value?: JSONContent }) => void
    allParentNodes?: FlowNode[]
    hasValue?: boolean
    hasValuePath?: boolean
}

export const VariableItemControlled: React.FC<VariableItemControlledProps> = ({
    item,
    opened,
    onChangeOpen,
    onDelete,
    onChange,
    allParentNodes,
    hasValue,
    hasValuePath
}) => {
    const { attributes, listeners, setNodeRef, transform, transition, active } = useSortable({
        id: item.id
    })

    const style: React.CSSProperties = useMemo(
        () => ({
            transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 }),
            transition,
            zIndex: active?.id === item.id ? 1 : 0
        }),
        [active?.id, item.id, transform, transition]
    )

    const innerTypeText = useMemo(() => {
        return innerTypeOptions.find(opt => opt.value === item.innerType)?.label
    }, [item.innerType])

    return (
        <SCxContainer {...attributes} style={style} ref={setNodeRef}>
            <SCxHeader>
                <SCxHeaderLeft>
                    <SCxHandle {...listeners} style={{ cursor: 'grab' }}>
                        <IconFont size={16} type="DotsSix" color="var(--color-gray-500)" />
                    </SCxHandle>
                    <SCxTitle>
                        {item.name}
                        <SCxTypeTitle>[{innerTypeText}]</SCxTypeTitle>
                    </SCxTitle>
                </SCxHeaderLeft>
                <SCxRightFill style={{ width: 'auto' }}>
                    <TagIcon icon="Trash" iconColor="var(--color-gray-500)" onClick={onDelete} />
                    <IconFont
                        size={16}
                        color="var(--color-gray-500)"
                        type={opened ? 'ArrowUpSmall' : 'ArrowDownSmall'}
                        onClick={() => onChangeOpen(!opened)}
                    />
                </SCxRightFill>
            </SCxHeader>
            <Collapse in={opened}>
                <SCxContent>
                    <SCxListItemBaseInfo>
                        <ListItem4ByecodeUi alignItems="center" justifyContent="space-between" styles={{ root: { gap: 0 } }}>
                            <Text>参数名称</Text>
                            <SCxRightFill>
                                <Input
                                    style={{ width: 180 }}
                                    value={item.name}
                                    onChange={v => onChange({ ...item, name: typeof v === 'string' ? v : String(v.target?.value ?? '') })}
                                />
                            </SCxRightFill>
                        </ListItem4ByecodeUi>

                        {hasValuePath && (
                            <ListItem4ByecodeUi alignItems="center" justifyContent="space-between" styles={{ root: { gap: 0 } }}>
                                <Text>参数值</Text>
                                <SCxRightFill>
                                    <Input
                                        style={{ width: 180 }}
                                        value={item.valuePath}
                                        onChange={v =>
                                            onChange({
                                                ...item,
                                                valuePath: typeof v === 'string' ? v : String(v.target?.value ?? '')
                                            })
                                        }
                                    />
                                </SCxRightFill>
                            </ListItem4ByecodeUi>
                        )}
                        <ListItem4ByecodeUi alignItems="center" justifyContent="space-between" styles={{ root: { gap: 0 } }}>
                            <Text>参数类型</Text>
                            <SCxRightFill>
                                <Select
                                    value={item.innerType}
                                    style={{ width: 180 }}
                                    options={innerTypeOptions}
                                    onChange={val =>
                                        onChange({ ...item, innerType: val as ArgumentsSchema['innerType'], value: undefined })
                                    }
                                />
                            </SCxRightFill>
                        </ListItem4ByecodeUi>
                        {hasValue && item.innerType && (
                            <ListItem4ByecodeUi alignItems="center" justifyContent="space-between" styles={{ root: { gap: 0 } }}>
                                <Text>参数值</Text>
                                <SCxRightFill>
                                    <SettingParam
                                        innerType={item.innerType}
                                        allParentNodes={allParentNodes}
                                        value={item.value}
                                        onChange={val => onChange({ ...item, value: val })}
                                    />
                                </SCxRightFill>
                            </ListItem4ByecodeUi>
                        )}
                    </SCxListItemBaseInfo>
                </SCxContent>
            </Collapse>
        </SCxContainer>
    )
}
